/**
 * API client — wraps all fetch calls to /api/*.
 * Credentials (session cookie) are always included.
 */

const BASE = '/api';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error((body as { error?: string }).error ?? res.statusText);
    (err as Error & { status: number }).status = res.status;
    throw err;
  }

  return res.json() as Promise<T>;
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authApi = {
  login: (password: string) =>
    request<{ ok: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  logout: () =>
    request<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  me: () =>
    request<{ authenticated: boolean; exp?: number }>('/auth/me'),
};

// ─── Dashboard ────────────────────────────────────────────────────────────────

export type DashboardData = {
  assets: {
    pensions: Record<string, number>;
    isas: Array<{ id: string; name: string; value_gbp: number }>;
    gia: Array<{ id: string; name: string; value_gbp: number }>;
    crypto: Array<{ id: string; name: string; value_gbp: number }>;
    nora_jisa_gbp: number | null;
    cash_savings_gbp: number;
    property_gbp: number;
  };
  liabilities: {
    mortgages: Array<{ id: number; label: string; lender: string; balance: number }>;
    credit_card_gbp: number;
  };
  totals: {
    total_assets: number;
    total_liabilities: number;
    net_worth_inc_property: number;
    net_worth_exc_property: number;
  };
  snapshots: Array<{
    snapshot_date: string;
    label: string;
    category: string;
    value_gbp: number;
  }>;
};

export const dashboardApi = {
  get: () => request<DashboardData>('/dashboard/'),
  lockMonth: () => request<{ ok: boolean; snapshot_date: string }>('/dashboard/lock-month', { method: 'POST' }),
  updateSettings: (data: { cash_savings_gbp?: number; credit_card_gbp?: number }) =>
    request<{ ok: boolean }>('/dashboard/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// ─── Portfolio ────────────────────────────────────────────────────────────────

export const portfolioApi = {
  list: () => request<Array<{ id: string; name: string; type: string; provider: string; owner: string }>>('/portfolio/'),
  holdings: (id: string) => request<{ portfolio: object; holdings: object[] }>(`/portfolio/${id}/holdings`),
  transactions: (id: string, limit = 200, offset = 0) =>
    request<{ portfolio: object; transactions: object[]; limit: number; offset: number }>(
      `/portfolio/${id}/transactions?limit=${limit}&offset=${offset}`,
    ),
  pensions: () => request<object>('/portfolio/pensions'),
  nora: () => request<{ nora: object | null; fund_price_gbp: number | null; total_value_gbp: number | null }>('/portfolio/nora'),
};

// ─── Mortgage ─────────────────────────────────────────────────────────────────

export type MortgageData = {
  mortgages: Array<{
    id: number;
    label: string;
    lender: string;
    balance: number;
    originalBalance: number;
    rate: number;
    monthlyPayment: number;
    termMonths: number;
    startDate: string;
    rateType: string;
    rateEndDate: string;
    propertyValue: number;
    history: Array<{ date: string; balance: number }>;
    overpayments: Array<{ date: string; amount: number }>;
    lumpSums: Array<{ date: string; amount: number }>;
  }>;
  summary: {
    property_value_gbp: number;
    total_balance_gbp: number;
    equity_gbp: number;
    ltv_percent: number;
  };
};

export const mortgageApi = {
  get: () => request<MortgageData>('/mortgage/'),
};

// ─── Import ───────────────────────────────────────────────────────────────────

export type ImportResult = {
  imported: number;
  skipped_duplicates: number;
  errors: string[];
  date_range: { from: string; to: string } | null;
};

export const importApi = {
  upload: async (file: File, portfolioId: string): Promise<ImportResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('portfolio_id', portfolioId);
    const res = await fetch('/api/import/', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error((body as { error?: string }).error ?? res.statusText);
    }
    return res.json() as Promise<ImportResult>;
  },
};
